import { HugeiconsIcon } from "@hugeicons/react";
import {
  Call02Icon,
  Cancel01Icon,
  SquareArrowExpand01Icon,
  User02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Avatar } from "shared/components/Avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { cx } from "shared/utils";
import {
  UNKNOWN_CALLER_LABEL,
  isCallerUnknown,
  transferSourceLabel
} from "shared/utils/transferred-call";
import { formatPhoneDisplay } from "../views/phone-numbers/lib/format";
import { incomingCallerLabel } from "./callDisplay";
import { useSoftphone, type IncomingCall } from "./SoftphoneProvider";
import { SOFTPHONE_OVERLAY_Z_CLASS } from "./stacking";
import { describeIncomingOutcome, useTeamFirstCountdown } from "./teamFirst";
import { AiAgentAvatar, OutcomeIcon } from "./TeamFirstParts";
import { useDraggableOverlay, type DragOffset } from "./useDraggableOverlay";

const ACTIVE_AVATAR_COLOR = "#29B6F6";

const incomingSourceLabel = (
  call: Pick<
    IncomingCall,
    | "addedByName"
    | "transferredFromNumber"
    | "transferredFromName"
    | "transferredByName"
  >
): string | undefined => {
  if (call.addedByName) return `Added by ${call.addedByName}`;
  const transferSource = transferSourceLabel(
    call.transferredFromNumber,
    call.transferredFromName,
    call.transferredByName
  );
  return transferSource ? `via ${transferSource}` : undefined;
};

interface IncomingCallPillProps {
  // Expand the banner back into the full incoming-call popup.
  onExpand: () => void;
  // Drag position, owned by the overlay so it survives minimize/expand and the
  // hand-off to the in-call card once the call is answered (CRM-1318).
  dragOffset: DragOffset;
  onDragOffsetChange: (offset: DragOffset) => void;
}

/**
 * Minimized incoming-call banner (CRM-905, Figma 23472:90562 — the collapsed
 * bar above the popup). Caller + "Incoming call" with the answer/decline pair;
 * clicking the bar re-opens the full popup, dragging it moves it off whatever
 * it covers while the call rings (CRM-1318).
 */
export function IncomingCallPill({
  onExpand,
  dragOffset,
  onDragOffsetChange
}: IncomingCallPillProps) {
  const {
    incomingCall,
    incomingOutcome,
    acceptIncoming,
    silenceIncoming,
    sendIncomingToAi
  } = useSoftphone();
  const countdown = useTeamFirstCountdown(incomingCall);

  const { nodeRef, dragClassName, style, dragProps } = useDraggableOverlay({
    offset: dragOffset,
    onOffsetChange: onDragOffsetChange
  });

  if (!incomingCall) return null;

  const callerUnknown = isCallerUnknown(
    incomingCall.fromNumber,
    incomingCall.transferredFromNumber
  );
  const displayNumber = callerUnknown
    ? UNKNOWN_CALLER_LABEL
    : formatPhoneDisplay(incomingCall.fromNumber);
  const leadName = incomingCall.leadName?.trim();
  // One line of context fits the banner: the matched name and the lead's
  // community (CRM-1655), else the teammate adding you (CRM-1818), else who
  // handed the call over (CRM-1393, CRM-1747).
  const displayName =
    incomingCallerLabel(incomingCall) || incomingSourceLabel(incomingCall);

  const outcomeCopy = describeIncomingOutcome(incomingOutcome);
  const ringing = !incomingOutcome;

  let centerText = "Incoming call";
  if (outcomeCopy) centerText = outcomeCopy.message;
  else if (countdown)
    centerText = `AI agent picks up in ${countdown.secondsLeft}s`;

  return (
    <div
      ref={nodeRef}
      role="status"
      aria-live="polite"
      style={style}
      {...dragProps}
      className={cx(
        "fixed bottom-4 right-4 flex items-center gap-3 rounded-full bg-content-1000 py-2 pl-2 pr-3 text-white shadow-lg",
        dragClassName,
        SOFTPHONE_OVERLAY_Z_CLASS
      )}
    >
      <button
        type="button"
        onClick={ringing ? onExpand : undefined}
        disabled={!ringing}
        aria-label="Expand incoming call"
        className="flex items-center gap-3 rounded-full pr-1 text-left disabled:cursor-default"
      >
        <Avatar
          name={leadName}
          size={32}
          className="text-xs font-semibold"
          fallbackText={<HugeiconsIcon icon={User02Icon} size={16} />}
          fallbackBackgroundColor={ACTIVE_AVATAR_COLOR}
        />
        <span className="flex flex-col leading-tight">
          <span className="text-sm font-medium">{displayNumber}</span>
          {displayName ? (
            <span
              title={displayName}
              className="max-w-56 truncate text-xs text-white/60"
            >
              {displayName}
            </span>
          ) : null}
        </span>
      </button>

      <span className="ml-1 max-w-[220px] text-sm text-white/80">
        {centerText}
      </span>

      {outcomeCopy ? (
        <OutcomeIcon tone={outcomeCopy.tone} size={32} />
      ) : (
        <div className={cx("flex items-center gap-3", !ringing && "invisible")}>
          {countdown ? (
            <Tooltip placement="top" delay={{ open: 300 }}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Send to AI agent"
                  onClick={sendIncomingToAi}
                  className="grid size-8 place-items-center rounded-full"
                >
                  <AiAgentAvatar size={32} />
                </button>
              </TooltipTrigger>
              <TooltipContent className="z-[1000] rounded-lg bg-alpha-800 px-3 py-2 text-sm text-white">
                Send to AI agent
              </TooltipContent>
            </Tooltip>
          ) : null}

          <button
            type="button"
            aria-label="Start call"
            onClick={acceptIncoming}
            className="grid size-8 place-items-center rounded-full bg-success-500 text-white transition hover:bg-success-600"
          >
            <HugeiconsIcon icon={Call02Icon} size={16} />
          </button>

          <button
            type="button"
            aria-label="Silence"
            onClick={silenceIncoming}
            className="grid size-8 place-items-center rounded-full bg-danger-500 text-white transition hover:bg-danger-600"
          >
            <HugeiconsIcon icon={Cancel01Icon} size={16} />
          </button>

          <button
            type="button"
            aria-label="Expand incoming call"
            onClick={onExpand}
            className="grid size-8 place-items-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <HugeiconsIcon icon={SquareArrowExpand01Icon} size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
